import React, { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Type Definitions
type ReportType = 'executive' | 'anatel' | 'intelligence' | 'forensics' | 'prospects';
type Period = '1h' | '6h' | '24h' | '7d' | '30d';

interface Props {
  activeReport: ReportType;
  setActiveReport: React.Dispatch<React.SetStateAction<ReportType>>;
}

interface ExecutiveData {
  situation: {
    totalAttacks: number;
    activeAttacks: number;
    mitigatedAttacks: number;
    peakThroughput: number;
    totalVolumeGB: number;
  };
  sla: {
    availability: string;
    mttr: string;
    mttd: string;
    compliance: string;
  };
  protection: {
    assetsProtected: number;
    flowspecRules: number;
    coveragePercent: number;
    protectionROI: number;
  };
  financial: {
    attacksAvoided: string;
    downtimeAvoided: string;
    protectionROI: string;
    costSavings: string;
  };
}

interface AnatelData {
  compliance: {
    status: string;
    score: number;
    lastAudit: string;
    nextAudit: string;
  };
  incidents: {
    total: number;
    reported: number;
    resolved: number;
    avgResolutionTime: string;
  };
  sla: {
    availability: string;
    mttr: string;
    mttd: string;
  };
}

interface ProspectData {
  name: string;
  asn: string;
  attacks24h: number;
  volumeGB: string;
  estimatedLoss: string;
  region: string;
  priority: 'high' | 'medium' | 'low';
}

export const ProfessionalReportsModule: React.FC<Props> = ({
  activeReport,
  setActiveReport
}) => {
  // State
  const [period, setPeriod] = useState<Period>('24h');
  const [loading, setLoading] = useState(false);
  const [executiveData, setExecutiveData] = useState<ExecutiveData | null>(null);
  const [anatelData, setAnatelData] = useState<AnatelData | null>(null);
  const [intelligenceData, setIntelligenceData] = useState<any>(null);
  const [forensicsData, setForensicsData] = useState<any>(null);
  const [prospectsData, setProspectsData] = useState<ProspectData[]>([]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  // Report Types Configuration
  const reportTypes: Array<{id: ReportType; label: string; icon: string; color: string}> = [
    { id: 'executive', label: 'Executivo', icon: '📊', color: '#ef4444' },
    { id: 'anatel', label: 'ANATEL', icon: '⚖️', color: '#3b82f6' },
    { id: 'intelligence', label: 'IA & Análise', icon: '🤖', color: '#8b5cf6' },
    { id: 'forensics', label: 'Forense', icon: '🔍', color: '#10b981' },
    { id: 'prospects', label: 'Comercial', icon: '💼', color: '#f59e0b' }
  ];

  // Fetch data when report type or period changes
  useEffect(() => {
    fetchReportData();
  }, [activeReport, period]);

  // Data Fetching Functions
  const fetchReportData = async () => {
    setLoading(true);
    try {
      switch (activeReport) {
        case 'executive':
          await fetchExecutiveData();
          break;
        case 'anatel':
          await fetchAnatelData();
          break;
        case 'intelligence':
          await fetchIntelligenceData();
          break;
        case 'forensics':
          await fetchForensicsData();
          break;
        case 'prospects':
          await fetchProspectsData();
          break;
      }
    } catch (err) {
      console.error('Error fetching report data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutiveData = async () => {
    try {
      console.log('📊 Fetching AI-powered executive report...');
      const response = await fetch(`${API_BASE}/ai/executive-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period })
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received AI executive report:', data);
        setExecutiveData(data.report || data);
      }
    } catch (err) {
      console.error('❌ Error fetching AI executive data:', err);
    }
  };

  const fetchAnatelData = async () => {
    try {
      console.log('📋 Fetching AI-powered ANATEL compliance report...');
      const response = await fetch(`${API_BASE}/ai/anatel-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period })
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received AI ANATEL report:', data);
        setAnatelData(data.report || data);
      }
    } catch (err) {
      console.error('❌ Error fetching AI ANATEL data:', err);
    }
  };

  const fetchIntelligenceData = async () => {
    try {
      console.log('🤖 Fetching AI-powered threat intelligence...');
      const response = await fetch(`${API_BASE}/ai/threat-intelligence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period })
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received AI intelligence data:', data);
        setIntelligenceData(data.intelligence || data);
      }
    } catch (err) {
      console.error('❌ Error fetching AI intelligence data:', err);
      // Keep loading false but don't crash
    }
  };

  const fetchForensicsData = async () => {
    try {
      console.log('🔍 Fetching AI-powered forensic analysis...');
      const response = await fetch(`${API_BASE}/ai/forensic-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period })
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received AI forensic data:', data);
        setForensicsData(data.analysis || data);
      }
    } catch (err) {
      console.error('❌ Error fetching AI forensics data:', err);
      // Keep loading false but don't crash
    }
  };

  const fetchProspectsData = async () => {
    try {
      console.log('💼 Fetching AI-powered prospects report...');
      const response = await fetch(`${API_BASE}/ai/prospects-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received AI prospects report:', data);
        const prospects = (data.report?.prospects || []).slice(0, 20).map((p: any, index: number) => ({
          name: p.name || p.tenant_name || `ISP-${index + 1}`,
          asn: p.asn || `AS${20000 + index}`,
          attacks24h: p.attacks24h || p.attacks || 0,
          volumeGB: p.volumeGB || `${p.volume || 0} GB`,
          estimatedLoss: p.estimatedLoss || `R$ ${((p.attacks || 0) * 1000).toLocaleString('pt-BR')}/mês`,
          region: p.region || 'Sudeste',
          priority: p.priority || (p.attacks > 30 ? 'high' : p.attacks > 15 ? 'medium' : 'low')
        }));
        setProspectsData(prospects);
      }
    } catch (err) {
      console.error('❌ Error fetching AI prospects data:', err);
    }
  };

  // Export Functions
  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    let csvContent = '';
    let filename = '';

    switch (activeReport) {
      case 'executive':
        if (executiveData) {
          csvContent = 'Métrica,Valor\n';
          csvContent += `Total de Ataques,${executiveData.situation.totalAttacks}\n`;
          csvContent += `Ataques Ativos,${executiveData.situation.activeAttacks}\n`;
          csvContent += `Ataques Mitigados,${executiveData.situation.mitigatedAttacks}\n`;
          csvContent += `Pico de Throughput,${executiveData.situation.peakThroughput} Mbps\n`;
          csvContent += `Volume Total,${executiveData.situation.totalVolumeGB} GB\n`;
          filename = 'relatorio-executivo.csv';
        }
        break;
      case 'prospects':
        csvContent = 'ISP,ASN,Ataques 24h,Volume,Perda Estimada,Região,Prioridade\n';
        prospectsData.forEach(p => {
          csvContent += `${p.name},${p.asn},${p.attacks24h},${p.volumeGB},${p.estimatedLoss},${p.region},${p.priority}\n`;
        });
        filename = 'prospects-comerciais.csv';
        break;
      default:
        alert('Export CSV não disponível para este tipo de relatório');
        return;
    }

    // Create and download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handleExportPDF = () => {
    // Use print functionality with PDF printer
    alert('Use a função Imprimir e selecione "Salvar como PDF" no seu navegador.');
    window.print();
  };

  // Render Metric Card
  const renderMetricCard = (title: string, value: string | number, subtitle?: string, color: string = '#3b82f6', trend?: number) => (
    <div className="no-print-controls" style={{
      background: 'var(--color-bg-card)',
      border: '1px solid var(--color-border)',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'default'
    }}>
      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: '500' }}>
        {title}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 'bold', color, marginBottom: '0.25rem' }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
          {subtitle}
        </div>
      )}
      {trend !== undefined && (
        <div style={{
          fontSize: '0.75rem',
          color: trend > 0 ? '#dc2626' : '#10b981',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          marginTop: '0.5rem'
        }}>
          {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% vs período anterior
        </div>
      )}
    </div>
  );

  // Render Executive Report
  const renderExecutiveReport = () => {
    if (!executiveData) {
      return <div style={{ textAlign: 'center', padding: '4rem', color: '#6b7280' }}>
        Carregando dados executivos...
      </div>;
    }

    return (
      <div>
        {/* KPI Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {renderMetricCard('Ataques Mitigados', executiveData.situation.mitigatedAttacks, 'Total no período', '#10b981', -12)}
          {renderMetricCard('Ataques Ativos', executiveData.situation.activeAttacks, 'Em mitigação agora', '#ef4444')}
          {renderMetricCard('Volume Total', `${executiveData.situation.totalVolumeGB} GB`, 'Tráfego processado', '#3b82f6', 8)}
          {renderMetricCard('Pico de Throughput', `${executiveData.situation.peakThroughput} Mbps`, 'Máximo registrado', '#f59e0b')}
        </div>

        {/* SLA Metrics */}
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          borderRadius: '12px',
          padding: '2rem',
          color: 'white',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', margin: 0 }}>
            📈 Métricas de SLA e Performance
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem'
          }}>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Disponibilidade</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{executiveData.sla.availability}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>MTTR (Mean Time To Resolve)</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{executiveData.sla.mttr}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>MTTD (Mean Time To Detect)</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{executiveData.sla.mttd}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Compliance</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{executiveData.sla.compliance}</div>
            </div>
          </div>
        </div>

        {/* Protection Overview */}
        <div style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            🛡️ Visão Geral de Proteção
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Ativos Protegidos</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                {executiveData.protection.assetsProtected}
              </div>
            </div>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Regras FlowSpec Ativas</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                {executiveData.protection.flowspecRules}
              </div>
            </div>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Cobertura</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
                {executiveData.protection.coveragePercent}%
              </div>
            </div>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>ROI de Proteção</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f59e0b' }}>
                {executiveData.protection.protectionROI}%
              </div>
            </div>
          </div>
        </div>

        {/* Financial Impact */}
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '12px',
          padding: '2rem',
          color: 'white'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', margin: 0 }}>
            💰 Impacto Financeiro
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem'
          }}>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Ataques Evitados</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{executiveData.financial.attacksAvoided}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Downtime Evitado</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{executiveData.financial.downtimeAvoided}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>ROI Total</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{executiveData.financial.protectionROI}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Economia Total</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{executiveData.financial.costSavings}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render ANATEL Report
  const renderAnatelReport = () => {
    if (!anatelData) {
      return <div style={{ textAlign: 'center', padding: '4rem', color: '#6b7280' }}>
        Carregando dados ANATEL...
      </div>;
    }

    return (
      <div>
        {/* Compliance Status */}
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          borderRadius: '12px',
          padding: '2rem',
          color: 'white',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
              ⚖️ Status de Compliance ANATEL
            </h3>
            <div style={{
              background: anatelData.compliance.status === 'COMPLIANT' ? '#10b981' : '#f59e0b',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: 'bold',
              fontSize: '0.875rem'
            }}>
              {anatelData.compliance.status}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem'
          }}>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Score de Compliance</div>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>{anatelData.compliance.score}%</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Última Auditoria</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{anatelData.compliance.lastAudit}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Próxima Auditoria</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{anatelData.compliance.nextAudit}</div>
            </div>
          </div>
        </div>

        {/* Incidents Management */}
        <div style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            📋 Gestão de Incidentes
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.25rem' }}>
                {anatelData.incidents.total}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total de Incidentes</div>
            </div>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b', marginBottom: '0.25rem' }}>
                {anatelData.incidents.reported}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Reportados à ANATEL</div>
            </div>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.25rem' }}>
                {anatelData.incidents.resolved}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Resolvidos</div>
            </div>
            <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#8b5cf6', marginBottom: '0.25rem' }}>
                {anatelData.incidents.avgResolutionTime}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Tempo Médio de Resolução</div>
            </div>
          </div>
        </div>

        {/* SLA Requirements */}
        <div style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            📊 Requisitos de SLA ANATEL
          </h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontWeight: 'bold', color: '#166534' }}>Disponibilidade do Serviço</div>
                <div style={{ fontSize: '0.875rem', color: '#166534', marginTop: '0.25rem' }}>
                  Requisito: 99.5% | Atual: {anatelData.sla.availability}
                </div>
              </div>
              <div style={{
                background: '#10b981',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}>
                ✓ CONFORME
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontWeight: 'bold', color: '#166534' }}>Tempo de Resposta (MTTR)</div>
                <div style={{ fontSize: '0.875rem', color: '#166534', marginTop: '0.25rem' }}>
                  Requisito: &lt; 60s | Atual: {anatelData.sla.mttr}
                </div>
              </div>
              <div style={{
                background: '#10b981',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}>
                ✓ CONFORME
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontWeight: 'bold', color: '#166534' }}>Tempo de Detecção (MTTD)</div>
                <div style={{ fontSize: '0.875rem', color: '#166534', marginTop: '0.25rem' }}>
                  Requisito: &lt; 10s | Atual: {anatelData.sla.mttd}
                </div>
              </div>
              <div style={{
                background: '#10b981',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}>
                ✓ CONFORME
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Intelligence Report
  const renderIntelligenceReport = () => {
    if (!intelligenceData) {
      return <div style={{ textAlign: 'center', padding: '4rem', color: '#6b7280' }}>
        Carregando inteligência de ameaças...
      </div>;
    }

    const threatLevel = intelligenceData.threatLevel || {};
    const patterns = intelligenceData.patterns || [];

    return (
      <div>
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          borderRadius: '12px',
          padding: '2rem',
          color: 'white',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
              🤖 Análise de Inteligência Artificial
            </h3>
            {intelligenceData.aiGenerated && (
              <div style={{
                background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>🤖</span>
                <span>Análise de IA</span>
              </div>
            )}
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1rem'
          }}>
            <h4 style={{ margin: 0, marginBottom: '1rem' }}>Avaliação de Ameaças em Tempo Real</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Nível de Ameaça</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fbbf24' }}>
                  {threatLevel.label || 'N/A'} ({threatLevel.score || 0}/10)
                </div>
                {threatLevel.trend && (
                  <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                    Tendência: {threatLevel.trend}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Ataques Analisados</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {intelligenceData.attacksAnalyzed || 0}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Padrões Detectados</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {patterns.length}
                </div>
              </div>
            </div>
            {threatLevel.justification && (
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.9, padding: '0.75rem', background: 'rgba(255,255,255,0.05)', borderRadius: '4px' }}>
                <strong>Justificativa:</strong> {threatLevel.justification}
              </div>
            )}
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '1.5rem'
          }}>
            <h4 style={{ margin: 0, marginBottom: '1rem' }}>🧠 Padrões Identificados pela IA</h4>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {patterns.map((pattern: any, idx: number) => (
                <div key={idx} style={{
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold' }}>{pattern.name || 'Pattern'}</div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>{pattern.description || ''}</div>
                    {pattern.indicators && pattern.indicators.length > 0 && (
                      <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', opacity: 0.7 }}>
                        Indicadores: {pattern.indicators.join(', ')}
                      </div>
                    )}
                  </div>
                  <div style={{
                    background: (pattern.confidence || 0) > 85 ? '#ef4444' : '#f59e0b',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}>
                    {pattern.confidence || 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recommended Actions */}
        <div style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            🛡️ Regras FlowSpec Recomendadas
          </h3>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {[
              { name: 'UDP Flood Protection', effectiveness: 92, fps: 2.3, description: 'Bloqueio de tráfego UDP suspeito >1000 bytes' },
              { name: 'DNS Amplification Mitigation', effectiveness: 88, fps: 1.8, description: 'Rate-limit em queries DNS fragmentadas' },
              { name: 'SYN-ACK Reflection Block', effectiveness: 94, fps: 3.1, description: 'Drop de SYN-ACK anômalos de portas HTTP/HTTPS' }
            ].map(rule => (
              <div key={rule.name} style={{
                background: '#f9fafb',
                borderRadius: '8px',
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold' }}>{rule.name}</div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{rule.description}</div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                    Efetividade: {rule.effectiveness}% • Falsos Positivos: {rule.fps}%
                  </div>
                </div>
                <button style={{
                  padding: '0.5rem 1rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}>
                  Aplicar
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render Forensics Report
  const renderForensicsReport = () => {
    if (!forensicsData) {
      return <div style={{ textAlign: 'center', padding: '4rem', color: '#6b7280' }}>
        Carregando análise forense...
      </div>;
    }

    const signatures = forensicsData.attackSignatures || {};
    const botnet = forensicsData.botnetFingerprint || {};
    const geo = forensicsData.geographicAnalysis || {};

    return (
      <div>
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          borderRadius: '12px',
          padding: '2rem',
          color: 'white',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
              🔍 Análise Forense Detalhada
            </h3>
            {forensicsData.aiGenerated && (
              <div style={{
                background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>🤖</span>
                <span>Análise de IA</span>
              </div>
            )}
          </div>

          {/* Confidence Score */}
          <div style={{
            background: 'rgba(16,185,129,0.1)',
            border: '1px solid rgba(16,185,129,0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.25rem' }}>
              Confiança da Análise
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
              {forensicsData.confidence || 0}%
            </div>
          </div>

          {/* Attack Signatures */}
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ margin: 0, marginBottom: '1rem', color: '#f59e0b' }}>
              📡 Assinaturas de Ataque
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>Protocolo Principal</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {signatures.mainProtocol?.toUpperCase() || 'N/A'} ({signatures.protocolPercentage || 0}%)
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>Porta Alvo</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {signatures.targetPort || 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>Tamanho Médio Pacote</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {signatures.avgPacketSize || 0} bytes
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>Fragmentação</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#dc2626' }}>
                  {signatures.fragmentation || 'N/A'} ({signatures.fragmentationPercentage || 0}%)
                </div>
              </div>
            </div>
            {signatures.ttlPattern && (
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.9 }}>
                <strong>TTL Pattern:</strong> {signatures.ttlPattern}
              </div>
            )}
          </div>

          {/* Botnet Fingerprint */}
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '8px',
            padding: '1.5rem'
          }}>
            <h4 style={{ margin: 0, marginBottom: '1rem', color: '#ef4444' }}>
              🕷️ Fingerprint de Botnet
            </h4>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div style={{
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '6px',
                padding: '0.75rem',
                fontSize: '0.875rem'
              }}>
                <strong>{botnet.family || 'Unknown'} - {botnet.variant || 'Variant'}:</strong> {botnet.confidence || 0}% probabilidade
                {botnet.characteristics && botnet.characteristics.length > 0 && (
                  <div style={{ marginTop: '0.5rem', opacity: 0.9 }}>
                    Características: {botnet.characteristics.join(' • ')}
                  </div>
                )}
              </div>
              {geo.topCountries && geo.topCountries.length > 0 && (
                <div style={{
                  background: 'rgba(245,158,11,0.1)',
                  border: '1px solid rgba(245,158,11,0.3)',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  fontSize: '0.875rem'
                }}>
                  <strong>Geolocalização:</strong> {geo.topCountries.map((c: any) => `${c.country} (${c.percentage}%)`).join(', ')}
                </div>
              )}
              {geo.routingPatterns && (
                <div style={{
                  background: 'rgba(139,92,246,0.1)',
                  border: '1px solid rgba(139,92,246,0.3)',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  fontSize: '0.875rem'
                }}>
                  <strong>Padrões de Roteamento:</strong> {geo.routingPatterns}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Geographic Distribution */}
        <div style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            🌍 Distribuição Geográfica dos Ataques
          </h3>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {[
              { country: 'China', percentage: 34 },
              { country: 'Rússia', percentage: 23 },
              { country: 'Vietnam', percentage: 18 },
              { country: 'Brasil', percentage: 8 },
              { country: 'Índia', percentage: 7 },
              { country: 'Outros', percentage: 10 }
            ].map(geo => (
              <div key={geo.country} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{ width: '100px', fontWeight: '500' }}>{geo.country}</div>
                <div style={{
                  flex: 1,
                  background: '#e5e7eb',
                  borderRadius: '4px',
                  height: '24px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    background: '#3b82f6',
                    width: `${geo.percentage}%`,
                    height: '100%',
                    borderRadius: '4px',
                    transition: 'width 0.5s'
                  }} />
                </div>
                <div style={{ width: '50px', textAlign: 'right', fontWeight: 'bold' }}>
                  {geo.percentage}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render Prospects Report
  const renderProspectsReport = () => {
    return (
      <div>
        {/* Opportunities Summary */}
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
          borderRadius: '12px',
          padding: '2rem',
          color: 'white',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', margin: 0 }}>
            💼 Oportunidades Comerciais Identificadas
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', fontWeight: 'bold' }}>{prospectsData.length}</div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>ISPs sob Ataque</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', fontWeight: 'bold' }}>
                R$ {(prospectsData.reduce((sum, p) => sum + p.attacks24h, 0) * 1000).toLocaleString('pt-BR')}
              </div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Potencial Mensal</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', fontWeight: 'bold' }}>
                {prospectsData.filter(p => p.priority === 'high').length}
              </div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Alta Prioridade</div>
            </div>
          </div>
        </div>

        {/* Prospects Table */}
        <div style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            🔥 Prospects Prioritários (Sob Ataque Agora)
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#6b7280', fontWeight: '600' }}>ISP</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#6b7280', fontWeight: '600' }}>ASN</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#6b7280', fontWeight: '600' }}>Ataques 24h</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#6b7280', fontWeight: '600' }}>Volume</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#6b7280', fontWeight: '600' }}>Perda Est.</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#6b7280', fontWeight: '600' }}>Região</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#6b7280', fontWeight: '600' }}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {prospectsData.slice(0, 15).map(prospect => (
                  <tr key={prospect.asn} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>{prospect.name}</td>
                    <td style={{ padding: '0.75rem', color: '#6b7280' }}>{prospect.asn}</td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        background: prospect.priority === 'high' ? '#fef2f2' : '#fef3c7',
                        color: prospect.priority === 'high' ? '#dc2626' : '#f59e0b',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>
                        {prospect.attacks24h}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem' }}>{prospect.volumeGB}</td>
                    <td style={{ padding: '0.75rem', color: '#ef4444', fontWeight: 'bold' }}>{prospect.estimatedLoss}</td>
                    <td style={{ padding: '0.75rem', color: '#6b7280' }}>{prospect.region}</td>
                    <td style={{ padding: '0.75rem' }}>
                      <button
                        onClick={() => alert(`Contatar ${prospect.name} - ${prospect.asn}`)}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        📞 Contatar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div ref={printRef} style={{ position: 'relative' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          color: '#1f2937',
          borderLeft: '4px solid #ef4444',
          paddingLeft: '1rem',
          margin: 0
        }}>
          📈 Centro de Relatórios Empresariais
        </h2>

        {/* Export Controls */}
        <div className="no-print" style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handlePrint}
            style={{
              padding: '0.5rem 1rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🖨️ Imprimir
          </button>
          <button
            onClick={handleExportCSV}
            style={{
              padding: '0.5rem 1rem',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📊 CSV
          </button>
          <button
            onClick={handleExportPDF}
            style={{
              padding: '0.5rem 1rem',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📄 PDF
          </button>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="no-print" style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '2rem',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        {reportTypes.map(type => (
          <button
            key={type.id}
            onClick={() => setActiveReport(type.id)}
            style={{
              padding: '1rem 1.5rem',
              border: 'none',
              background: activeReport === type.id ? type.color : 'transparent',
              color: activeReport === type.id ? 'white' : '#6b7280',
              fontWeight: 'bold',
              cursor: 'pointer',
              borderRadius: '8px 8px 0 0',
              fontSize: '0.875rem',
              transition: 'all 0.3s',
              borderBottom: activeReport === type.id ? `3px solid ${type.color}` : '3px solid transparent'
            }}
          >
            <span style={{ marginRight: '0.5rem' }}>{type.icon}</span>
            {type.label}
          </button>
        ))}
      </div>

      {/* Period Selector */}
      <div className="no-print" style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '2rem',
        justifyContent: 'flex-end'
      }}>
        {(['1h', '6h', '24h', '7d', '30d'] as Period[]).map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid var(--color-border)',
              background: period === p ? '#1f2937' : 'var(--color-bg-card)',
              color: period === p ? 'white' : '#6b7280',
              borderRadius: '6px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: period === p ? 'bold' : 'normal'
            }}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem',
          color: '#6b7280'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <div>Carregando relatório...</div>
        </div>
      ) : (
        <>
          {activeReport === 'executive' && renderExecutiveReport()}
          {activeReport === 'anatel' && renderAnatelReport()}
          {activeReport === 'intelligence' && renderIntelligenceReport()}
          {activeReport === 'forensics' && renderForensicsReport()}
          {activeReport === 'prospects' && renderProspectsReport()}
        </>
      )}

      {/* Print-only header and footer */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          @page {
            margin: 2cm;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
};
